import {apiDelete, apiGet, apiPatch, apiPost, apiPut} from '@features/auth/api/apiClient';
import {
  AddNewAttachmentRequest,
  AddNewNoteRequest,
  AlertButtonState,
  ApiActionResponse,
  AssetAlert,
  AssetAlertsApiResponse,
  AttachmentCategory,
  AttachmentsList,
  BeginRepairRequest,
  BeginRepairResponse,
  PauseRepairRequest,
  ChangeWOStatus,
  ChangeWOStatusResponse,
  ConfirmWOStatus,
  CustomMessageProps,
  EditNoteRequest,
  EndRepairRequest,
  EndRepairResponse,
  GetMeterDetailRequest,
  GetMessagesRequest,
  GetMessagesResponse,
  LatestRepairAlert,
  MeterDetail,
  MeterItem,
  MessageResponse,
  NotesList,
  PartHistoryApiResponse,
  partsHistoryTabs,
  PartsListResponse,
  RepairReasonStatusesProps,
  RequestAuthenticateParams,
  RequestAuthenticateResponse,
  RequestRow,
  SubmitPartsRequest,
  TasksList,
  TasksProps,
  UpdateMessageRequest,
  UpdateMeterRequest,
  UpdateMeterResponse,
  UpdateRepairReasonRequest,
  updateTaskRequest,
  UpdateWorkOrderRequest,
  WorkOrderStatusesProps,
  GetAssetAttachmentsParams,
  AttachmentsPageResult,
  RepairHours,
} from './RepairDetailsScreenProps';
import {endPoints} from '@features/auth/constants/endPoints';
import {ApiResponse} from '@shared/types/api';
import {globalErrorHandler} from '@shared/error/GlobalErrorController';
import {
  ConfiguredRepairParts,
  ConfiguredRepairPartsPageResult,
  ConfiguredRepairPartsRequest,
  RepairItem,
  RepairParts,
  RepairPartsRequest,
  RepairPartsResponse,
} from '@features/dashboard/components/WorkOrders/WorkOrderProps';
import {appConstants, mockedCreatedUserID} from '@shared/constants/global';
import repairDetails from './mocks/repairsDetails.json';
import {
  attachmentTypes,
  noteTypes,
  resolutionStatuses,
} from './constants/repairDetails.constant';

/**
 * This method is used to get the completed task count
 * @param {tasks}  - Tasks list
 * @return {TasksList[]}
 */
export const getCompletedTaskCount = (tasks: TasksList[]): TasksList[] => {
  if (!Array.isArray(tasks)) {
    return [];
  }
  return tasks.filter((task: TasksList) => task?.resultName !== '');
};

/**
 * This method is used to fetch tasks list of the selected repair
 * @param {repairId} - number
 * @returns {Promise<TasksList[]>}
 */
export const getTasksList = async (
  repairId: string,
): Promise<TasksList[] | string> => {
  try {
    const {data} = await apiGet<TasksList[] | string>({
      endpoint: `${endPoints.repairDetails.tasks}${repairId}`,
    });
    const response: ApiResponse<TasksList[] | string> = data;

    if (response?.success && response?.data) {
      return response?.data;
    } else {
      return response?.message;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch tasks list of the selected repair
 * @param {repairId} - number
 * @returns {Promise<TasksList[]>}
 */
export const updateTasksList = async (
  repairId: number,
  task: TasksList,
  resultId: number,
  comment: string,
  technicianId: number
): Promise<ApiActionResponse | undefined> => {
  try {
    const payload = {stepNumber: task?.stepNumber, resultId, comment, technicianId};
    const {data} = await apiPut<string>({
      endpoint: `${endPoints.repairDetails.updateTask}${repairId}`,
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: (response?.success ? response?.data : response?.message) ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch repair details of the selected repair
 * @param {repairId} - number
 * @returns {Promise<RepairItem[]>}
 */
export const getRepairDetails = async (
  repairId: string,
): Promise<RepairItem | undefined> => {
  try {
    const {data} = await apiGet<RepairItem>({
      endpoint: `${endPoints.repairDetails.repairData}${repairId}`,
    });
    const response: ApiResponse<RepairItem> = data;

    if (response) {
      return response;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch repair hours for the selected repair and technician.
 * Endpoint: GET /api/WorkOrderRepairs/{repairId}/repair-hours?technicianId={technicianId}
 * Returns accumulated hours, time-standard display, and overtime warning data that were
 * previously included in getRepairDetails but have been separated into a dedicated API.
 * @param {repairId} - string
 * @param {technicianId} - number
 * @returns {Promise<RepairHours | undefined>}
 */
export const getRepairHours = async (
  repairId: string,
  technicianId: number,
): Promise<RepairHours | undefined> => {
  try {
    const {data} = await apiGet<RepairHours>({
      endpoint: `${endPoints.repairDetails.repairHours(Number(repairId), technicianId)}`,
    });
    const response: ApiResponse<RepairHours> = data;

    if (response?.success && response?.data) {
      return response?.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to get the repair parts list
 * @param {repairId} - number
 * @param {pageNumber} - number
 * @param {pageSize} - number
 * @returns
 */
export const getRepairPartsList = async ({
  repairId,
  pageNumber = 1,
  pageSize = 10,
}: RepairPartsRequest): Promise<RepairParts[] | undefined> => {
  try {
    const queryParams = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    let response: ApiResponse<RepairParts[]>;
    // if (!appConstants.isMock) {
    //   return {
    //     items: repairDetails?.data?.repairDetails?.parts?.configuredPartsList,
    //   };
    // }
    const {data} = await apiGet<RepairParts[]>({
      endpoint: `${endPoints?.repairDetails?.repairParts(
        repairId,
      )}${queryParams.toString()}`,
    });
    response = data;

    if (response.data) {
      if (response?.data) {
        return response?.data;
      }
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to get the repair parts list
 * @param {repairId} - number
 * @param {pageNumber} - number
 * @param {pageSize} - number
 * @returns
 */
export const getConfiguredRepairPartsList = async ({
  technicianId,
  documentId,
  userId,
  repairId,
  assetId,
  pageNumber = 1,
  pageSize = 9,
  sortBy,
  isAscending,
}: ConfiguredRepairPartsRequest): Promise<
  ConfiguredRepairPartsPageResult | undefined
> => {
  try {
    const payload: Record<string, unknown> = {
      technicianId,
      documentId,
      userId,
      repairId,
      assetId,
      pageNumber,
      pageSize,
    };
    if (sortBy) {
      payload.sortBy = sortBy;
    }
    if (isAscending !== undefined) {
      payload.isAscending = isAscending;
    }

    const {data} = await apiPost<ConfiguredRepairParts[]>({
      endpoint: `${endPoints?.repairDetails?.configuredRepairParts}`,
      payload,
    });
    const response = data as any;

    if (response?.data) {
      let sortedData = response.data as ConfiguredRepairParts[];
      return {
        data: sortedData,
        pagination: response.pagination ?? {
          currentPage: pageNumber,
          pageSize,
          totalItems: (response.data as ConfiguredRepairParts[]).length,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch notes list of the selected repair
 * @param {repairId} - number
 * @returns {Promise<NotesList[]>}
 */
export const getNotesList = async (
  repairId: string,
  searchValue?: string,
  timezone?: string,
): Promise<NotesList[] | string> => {
  try {
    const queryParamsObj: Record<string, string> = {};
    if (searchValue) {
      queryParamsObj.searchString = searchValue;
    }
    // timeZone is always required by the API — send it even when empty
    queryParamsObj.timeZone = timezone ?? '';
    const queryParams = new URLSearchParams(queryParamsObj);
    const queryString = queryParams.toString();
    const {data} = await apiGet<NotesList[] | string>({
      endpoint: queryString
        ? `${endPoints.repairDetails.notes}${repairId}&${queryString}`
        : `${endPoints.repairDetails.notes}${repairId}`,
    });
    const response: ApiResponse<NotesList[] | string> = data;
    if (response?.success && response?.data) {
      return response?.data;
    } else {
      return response?.message;
    }
  } catch (error: any) {
    //console.log('response from getNotesList', repairId, searchValue, error)
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch a single attachment by assetId and attachmentId
 * @param {assetId} - string | number
 * @param {attachmentId} - string | number
 * @returns {Promise<AttachmentsList | string>}
 */
export const getAssetAttachmentById = async (
  assetId: string | number,
  attachmentId: string | number,
): Promise<AttachmentsList | string> => {
  try {
    const {data} = await apiGet<AttachmentsList>({
      endpoint: `${endPoints.repairDetails.getAssetAttachmentById(assetId, attachmentId)}`,
    });
    const response: ApiResponse<AttachmentsList> = data;
    if (response?.success && response?.data) {
      return response?.data;
    } else {
      return response?.message ?? '';
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch a single attachment by workOrderId and attachmentId.
 * Used when the attachment is a Work Order attachment (isWOAttachment === 1).
 * Endpoint: GET /api/workorders/{workOrderId}/attachments/{attachmentId}
 * @param {workOrderId} - string | number
 * @param {attachmentId} - string | number
 * @returns {Promise<AttachmentsList | string>}
 */
export const getWorkOrderAttachmentById = async (
  workOrderId: string | number,
  attachmentId: string | number,
): Promise<AttachmentsList | string> => {
  try {
    const {data} = await apiGet<AttachmentsList>({
      endpoint: `${endPoints.repairDetails.getWorkOrderAttachmentById(workOrderId, attachmentId)}`,
    });
    const response: ApiResponse<AttachmentsList> = data;
    if (response?.success && response?.data) {
      return response?.data;
    } else {
      return response?.message ?? '';
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch attachments list for a given asset/workorder.
 * Supports server-side pagination, sorting, and filtering via query params.
 * Defaults: page=1, pageSize=9, sortBy='fileNameWithExtension', sortOrder='ascending', filterBy='All'
 * @param {assetId} - string | number
 * @param {workOrderId} - string | number
 * @param {params} - optional sort/filter/pagination params
 * @returns {Promise<AttachmentsPageResult | string>}
 */
export const getAssetAttachments = async (
  assetId: string | number,
  workOrderId: string | number,
  params?: GetAssetAttachmentsParams,
): Promise<AttachmentsPageResult | string> => {
  try {
    const queryParams = {
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 9,
      sortBy: params?.sortBy ?? 'modifiedDate',
      sortOrder: params?.sortOrder ?? 'ascending',
      filterBy: params?.filterBy ?? 'All',
    };
    const {data} = await apiGet<AttachmentsList[]>({
      endpoint: `${endPoints.repairDetails.getAssetAttachments(assetId, workOrderId)}`,
      params: queryParams,
    });
    const response = data as any;
    if (response?.success && response?.data) {
      return {
        data: response.data as AttachmentsList[],
        pagination: response.pagination ?? {
          currentPage: queryParams.page,
          pageSize: queryParams.pageSize,
          totalItems: (response.data as AttachmentsList[]).length,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    } else {
      return response?.message;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch attachment categories.
 * Errors are swallowed here (non-critical UI data) — callers should handle
 * an undefined return value gracefully.
 * @returns {Promise<AttachmentCategory[] | undefined>}
 */
export const getAttachmentCategories = async (): Promise<
  AttachmentCategory[] | undefined
> => {
  try {
    const {data} = await apiGet<AttachmentCategory[]>({
      endpoint: endPoints.repairDetails.getAttachmentCategories,
    });
    const response: ApiResponse<AttachmentCategory[]> = data;
    if (response?.success && response?.data && Array.isArray(response.data)) {
      return response.data;
    }
  } catch {
    // Non-critical: silently swallow category fetch errors
  }
};

/**
 * This method is used to get status of the selected task
 * @param text
 * @returns
 */
export const getStatus = (text: string) => {
  const item = resolutionStatuses.find(
    resolutionStatus => resolutionStatus?.value === text,
  );
  return item ? item.status : null;
};

/**
 * This method is used to fetch work order status
 * @param {repairId} - number
 * @returns {Promise<NotesList[]>}
 */
export const getWorkOrderStatus = async (): Promise<
  WorkOrderStatusesProps[] | undefined
> => {
  try {
    const {data} = await apiGet<WorkOrderStatusesProps[]>({
      endpoint: `${endPoints.repairDetails.workOrderStatus}`,
    });
    const response: ApiResponse<WorkOrderStatusesProps[]> = data;

    if (response?.success && response?.data) {
      return response?.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to add the new note
 * @param {repairId} - number
 * @returns {Promise<TasksList[]>}
 */
export const addNewNote = async ({
  repairId,
  subject,
  note,
  createdTechnicianID,
  createdUserID,
  isDocument,
}: AddNewNoteRequest): Promise<ApiActionResponse | undefined> => {
  try {
    const payload = {
      id: repairId,
      subject,
      note,
      createdTechnicianID,
      createdUserID,
      isDocument,
      isPending: false,
    };
    const {data} = await apiPost<string>({
      endpoint: `${endPoints.repairDetails.addNewNote}`,
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch notes list of the selected repair
 * @param {repairId} - number
 * @returns {Promise<NotesList[]>}
 */
export const getPartsList = async (
  searchText: string,
  assetId: number
): Promise<PartsListResponse | undefined> => {
  try {
    // if (appConstants?.isMock) {
    //   // MOCK: return 2000 mock parts for performance testing
    //   const mockParts = Array.from({length: 2000}, (_, i) => ({
    //     partID: i + 1,
    //     partNumber: `PN-${String(i + 1).padStart(5, '0')}`,
    //     partName: `Part Name ${i + 1}`,
    //     availableQty: i % 3 === 0 ? 0 : 10 + (i % 50),
    //     storeroomDesc: `Storeroom ${Math.floor(i / 50) + 1}`,
    //     storeRoom: `SR${Math.floor(i / 50) + 1}`,
    //     qty: 1,
    //   }));
    //   return {parts: mockParts} as unknown as PartsListResponse;
    // } else {
      const queryParams = new URLSearchParams({
        searchText: searchText,
        assetId: assetId?.toString()
      });
      const {data} = await apiGet<PartsListResponse>({
        endpoint: `${
          endPoints?.repairDetails?.getPartsList
        }${queryParams.toString()}`,
      });
      const response: ApiResponse<PartsListResponse> = data;

      if (response && response) {
        return response;
      }
    //}
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to update the work order status
 * @param {woId} - number
 * @param {woStatus} - string
 * @returns {Promise<boolean | undefined>}
 */
export const updateWorkOrderStaus = async ({
  woId,
  woStatus,
}: UpdateWorkOrderRequest): Promise<string | undefined> => {
  try {
    const {data} = await apiPatch<string>({
      endpoint: `${endPoints?.repairDetails?.updateWorkOrderStatus(
        woId,
        woStatus,
      )}`,
    });
    const response: ApiResponse<string> = data;

    if (response?.success && response?.data) {
      return response?.message;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to update the work order status
 * @param {woId} - number
 * @param {woStatus} - string
 * @returns {Promise<boolean | undefined>}
 */
export const changeWorkOrderStaus = async ({
  workOrderId,
  statusCode,
  technicianId,
  userId,
}: ChangeWOStatus): Promise<ChangeWOStatusResponse | undefined> => {
  try {
    const payload = {
      workOrderId,
      statusCode,
      technicianId,
      userId,
    };
    const {data} = await apiPatch<ChangeWOStatusResponse>({
      endpoint: `${endPoints?.repairDetails?.changeWOStatus}`,
      payload,
    });
    const response: ApiResponse<ChangeWOStatusResponse> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to confirm to complete the repair and then complete the WO
 * @param {woId} - number
 * @param {woStatus} - string
 * @returns {Promise<boolean | undefined>}
 */
export const confirmWOStatus = async ({
  workOrderId,
  newStatus,
  technicianId,
  userId,
  repairId,
}: ConfirmWOStatus): Promise<ChangeWOStatusResponse | undefined> => {
  try {
    const payload = {
      workOrderId,
      newStatus,
      technicianId,
      userId,
      repairId,
    };
    const {data} = await apiPost<ChangeWOStatusResponse>({
      endpoint: `${endPoints?.repairDetails?.confirmWOStatus}`,
      payload,
    });
    const response: ApiResponse<ChangeWOStatusResponse> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch work order status
 * @param {repairId} - number
 * @returns {Promise<RepairReasonStatusesProps[]>}
 */
export const getRepairReasonStatus = async (): Promise<
  RepairReasonStatusesProps[] | undefined
> => {
  try {
    const {data} = await apiGet<RepairReasonStatusesProps[]>({
      endpoint: `${endPoints.repairDetails.repairReasonStatus}`,
    });
    const response: ApiResponse<RepairReasonStatusesProps[]> = data;
    if (response?.success && response?.data) {
      return response?.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to update the work order status
 * @param {repairId} - number
 * @param {woStatus} - string
 * @returns {Promise<boolean | undefined>}
 */
export const updateRepairReasonStatus = async ({
  repairId,
  repairReasonId,
}: UpdateRepairReasonRequest): Promise<ChangeWOStatusResponse | undefined> => {
  try {
    const {data} = await apiPatch<string>({
      endpoint: `${endPoints?.repairDetails?.updateRepairReasonStatus(
        repairId,
        repairReasonId,
      )}`,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch tasks list of the selected repair
 * @param {repairId} - number
 * @returns {Promise<TasksList[]>}
 */
export const submitPartsRequest = async (
  requests: SubmitPartsRequest[],
): Promise<ApiActionResponse | undefined> => {
  try {
    const payload = requests.map(
      ({
        repairId,
        partId,
        qty,
        technicianId,
        storeRoom,
        createdUserID,
        isOwnRequest,
        requestComment,
      }) => ({
        repairId,
        partId,
        technicianId,
        storeRoom,
        requestedQty: qty,
        requestPartStatusID: 1,
        createdUserID,
        isOwnRequest,
        requestComment,
      }),
    );
    const {data} = await apiPost<string>({
      endpoint: `${endPoints.repairDetails.submitParts}`,
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to begin the repair
 * @param {technicianId}
 * @param {repairId}
 * @param {isLabor}
 * @param {isWOStatusToActive}
 * @returns {BeginRepairResponse}
 */
export const beginRepair = async ({
  technicianId,
  repairId,
  isLabor,
  isWOStatusToActive,
  IsLaborOvertimeCheckRequired,
  IsTechnicianOvertimeCheckRequired,
  isWorkorderStatusCheckRequired,
  userTimeZone,
  technicianDeclinedOvertime = false,
  isIndirectOvertimeCheckRequired = true,
}: BeginRepairRequest): Promise<boolean | string> => {
  try {
    let response: ApiResponse<boolean | string>;
    const payload = {
      technicianId: technicianId.toString(),
      isLabor: isLabor,
      isWOStatusToActive: isWOStatusToActive,
      IsLaborOvertimeCheckRequired: IsLaborOvertimeCheckRequired,
      IsTechnicianOvertimeCheckRequired: IsTechnicianOvertimeCheckRequired,
      IsWorkorderStatusCheckRequired: isWorkorderStatusCheckRequired,
      UserTimeZone: userTimeZone || '',
      technicianDeclinedOvertime: technicianDeclinedOvertime,
      isIndirectOvertimeCheckRequired: isIndirectOvertimeCheckRequired,
    };
    const {data} = await apiPost<boolean | string>({
      endpoint: `${endPoints?.repairDetails?.beginRepair(repairId)}`,
      payload,
    });
    response = data;

    if (response?.success) {
      return response?.success;
    } else {
      return response?.message;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to pause an active repair for a technician.
 * Endpoint: POST /api/technicians/{technicianId}/pause-repair
 * @param {technicianId} - ID of the technician (used in the URL)
 * @param {userId} - ID of the logged-in user
 * @param {isLaborOvertimeCheckRequired} - whether labor overtime check is required (default true)
 * @param {isPromptOvertimeCheckRequired} - whether prompt overtime check is required (default true)
 * @param {userTimeZone} - IANA or Windows timezone string of the user
 * @returns {Promise<boolean | string>} true on success, error message string on failure
 */
export const pauseRepair = async ({
  technicianId,
  userId,
  isLaborOvertimeCheckRequired = true,
  isPromptOvertimeCheckRequired = true,
  userTimeZone,
}: PauseRepairRequest): Promise<boolean | string> => {
  try {
    let response: ApiResponse<boolean | string>;
    const payload = {
      userId,
      isLaborOvertimeCheckRequired,
      isPromptOvertimeCheckRequired,
      userTimeZone: userTimeZone || '',
    };
    const {data} = await apiPost<boolean | string>({
      endpoint: `${endPoints?.repairDetails?.pauseRepair(technicianId)}`,
      payload,
    });
    response = data;

    if (response?.success) {
      return response?.success;
    } else {
      return response?.message ?? '';
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to begin the repair
 * @param {technicianId}
 * @param {maintShopId}
 * @param {repairId}
 * @returns {EndRepairResponse}
 */
export const endRepair = async ({
  technicianId,
  maintShopId,
  repairId,
  isLaborOvertimeCheckRequired,
  isPromptOvertimeCheckRequired,
  userTimeZone,
  isPmMeterReadingCheckRequired,
  hasSaViewPermission,
  userId,
  isCheckWorkOrderStatusRequired,
  proceedToChangeWorkOrderStatus,
  userEmail,
}: EndRepairRequest): Promise<EndRepairResponse | undefined> => {
  try {
    let response: ApiResponse<EndRepairResponse>;
    const payload = {
      technicianId: Number(technicianId),
      maintShopId: Number(maintShopId),
      repairId: Number(repairId),
      isLaborOvertimeCheckRequired: isLaborOvertimeCheckRequired ?? true,
      isPromptOvertimeCheckRequired: isPromptOvertimeCheckRequired ?? true,
      userTimeZone: userTimeZone || '',
      isPmMeterReadingCheckRequired: isPmMeterReadingCheckRequired ?? true,
      hasSaViewPermission: hasSaViewPermission ?? false,
      userId: userId ?? 0,
      isCheckWorkOrderStatusRequired: isCheckWorkOrderStatusRequired ?? true,
      proceedToChangeWorkOrderStatus: proceedToChangeWorkOrderStatus ?? false,
      userEmail: userEmail ?? '',
    };
    const {data} = await apiPost<EndRepairResponse>({
      endpoint: `${endPoints?.repairDetails?.endRepair}`,
      payload,
    });
    response = data;

    if (response) {
      return response;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This message is used to get the custom message, When the notes list is empty pr search result is empty
 * @param notes
 */
export const getCustomMessage = (
  filteredNotes: NotesList[],
  selectedTab: string,
  searchText: string,
): CustomMessageProps => {
  let message: CustomMessageProps = {
    title: '',
    desc: '',
  };
  if (searchText === '') {
    if (filteredNotes?.length === 0 && selectedTab === noteTypes?.all) {
      message = {
        title: 'common.feedback.notes.noNotes.title',
        desc: 'common.feedback.notes.noNotes.subTitle',
      };
    } else if (
      filteredNotes?.length === 0 &&
      selectedTab === noteTypes?.repair
    ) {
      message = {
        title: 'common.feedback.notes.noRepairNotes.title',
        desc: 'common.feedback.notes.noRepairNotes.subTitle',
      };
    } else if (
      filteredNotes?.length === 0 &&
      selectedTab === noteTypes?.workOrder
    ) {
      message = {
        title: 'common.feedback.notes.noWorkOrderNotes.title',
        desc: 'common.feedback.notes.noWorkOrderNotes.subTitle',
      };
    } else if (
      filteredNotes?.length === 0 &&
      selectedTab === noteTypes?.workOrder
    ) {
      message = {
        title: 'common.feedback.notes.noWorkOrderNotes.title',
        desc: 'common.feedback.notes.noWorkOrderNotes.subTitle',
      };
    }
  } else {
    if (filteredNotes?.length === 0) {
      message = {
        title: 'common.feedback.notes.noResults.title',
        desc: 'common.feedback.notes.noResults.subTitle',
      };
    }
  }

  return message;
};

export interface GetPartsHistoryRequest {
  repairId: string;
  pageNumber?: number;
  pageSize?: number;
  statusType?: string;
}

/**
 * @param repairId   - repair ID (converted to number for the URL)
 * @param pageNumber - 1-based page index (default 1)
 * @param pageSize   - items per page (default 10)
 * @param statusType - status label to filter by (e.g. "On Order")
 */
export const getPartsHistory = async ({
  repairId,
  pageNumber = 1,
  pageSize = 10,
  statusType,
}: GetPartsHistoryRequest): Promise<PartHistoryApiResponse | undefined> => {
  try {
    const payload = {
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      statusType: statusType ?? '', // API may require the param to be present even if empty
    };

    const {data} = await apiGet<PartHistoryApiResponse>({
      endpoint: `${endPoints?.repairDetails?.partsHistory(repairId)}`,
      params: payload,
    });

    const response = data as unknown as PartHistoryApiResponse;
    if (response?.data && Array.isArray(response.data)) {
      return response;
    }
    return undefined;
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};
/**
 * Fetches the list of available part-request statuses
 * @returns Array of { requestPartStatusId, requestPartStatus }
 */
export const getPartsHistoryStatus = async (): Promise<
  partsHistoryTabs[] | undefined
> => {
  try {
    const {data} = await apiGet<partsHistoryTabs[]>({
      endpoint: endPoints?.repairDetails?.partsHistoryStatus,
    });
    const response: ApiResponse<partsHistoryTabs[]> = data;
    if (
      response?.data &&
      Array.isArray(response.data) &&
      response.data.length > 0
    ) {
      return response.data;
    }
    if (
      Array.isArray(data) &&
      (data as unknown as partsHistoryTabs[]).length > 0
    ) {
      return data as unknown as partsHistoryTabs[];
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Converts a raw byte count (number or numeric string) to a human-readable size.
 * Values < 1 MB are shown in KB; values ≥ 1 MB are shown in MB (2 decimal places).
 * e.g. 68454 → "66.85 KB",  2097152 → "2.00 MB"
 */
export const formatAttachmentDate = (apiDate?: string | null): string => {
  if (!apiDate) return '';
  const date = new Date(apiDate);
  if (isNaN(date.getTime())) return apiDate;
  const datePart = date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
  });
  const yearPart = date.toLocaleString('en-US', {
    year: 'numeric',
  });
  const timePart = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart} ${yearPart}, ${timePart}`;
};

/**
 * Formats an ISO date string into DD/MM/YY, HH:MMAM format for the Alerts table.
 * e.g. "2025-12-01T06:23:38" → "01/12/25, 06:23AM"
 */
export const formatAlertDate = (apiDate?: string | null): string => {
  if (!apiDate) return '--';
  const date = new Date(apiDate);
  if (isNaN(date.getTime())) return apiDate;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const hours24 = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  const hoursStr = String(hours12).padStart(2, '0');
  return `${day}/${month}/${year}, ${hoursStr}:${minutes}${ampm}`;
};

export const formatFileSize = (size?: string | number | null): string => {
  const bytes =
    typeof size === 'string' ? parseFloat(size) : (size ?? 0);
  if (!bytes || isNaN(bytes)) return '0 KB';
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/** Image file extensions that can be rendered by React Native's <Image>. */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

/**
 * Returns true when the supplied file extension belongs to a renderable image type.
 * Comparison is case-insensitive.
 */
export const isImageExtension = (ext?: string | null): boolean => {
  if (!ext) return false;
  return IMAGE_EXTENSIONS.includes(ext.toLowerCase());
};

/**
 * This message is used to get the custom message, When the attachment list is empty pr search result is empty
 * @param attachments
 */
export const getCustomMessageForAttachments = (
  filteredAttachments: AttachmentsList[],
  selectedTab: string,
  searchText: string,
): CustomMessageProps => {
  let message: CustomMessageProps = {
    title: '',
    desc: '',
  };
  if (searchText === '') {
    if (
      filteredAttachments?.length === 0 &&
      selectedTab === attachmentTypes?.all
    ) {
      message = {
        title: 'common.feedback.attachments.noAttachments.title',
        desc: 'common.feedback.attachments.noAttachments.subTitle',
      };
    } else if (
      filteredAttachments?.length === 0 &&
      selectedTab === attachmentTypes?.asset
    ) {
      message = {
        title: 'common.feedback.attachments.noRepairAttachments.title',
        desc: 'common.feedback.attachments.noRepairAttachments.subTitle',
      };
    } else if (
      filteredAttachments?.length === 0 &&
      selectedTab === attachmentTypes?.workOrder
    ) {
      message = {
        title: 'common.feedback.attachments.noWorkOrderAttachments.title',
        desc: 'common.feedback.attachments.noWorkOrderAttachments.subTitle',
      };
    } else if (
      filteredAttachments?.length === 0 &&
      selectedTab === attachmentTypes?.workOrder
    ) {
      message = {
        title: 'common.feedback.attachments.noWorkOrderAttachments.title',
        desc: 'common.feedback.attachments.noWorkOrderAttachments.subTitle',
      };
    }
  } else {
    if (filteredAttachments?.length === 0) {
      message = {
        title: 'common.feedback.attachments.noResults.title',
        desc: 'common.feedback.attachments.noResults.subTitle',
      };
    }
  }

  return message;
};
/**
 * Truncates a file name that exceeds 20 characters.
 * Format: first8chars....last8charsOfBaseName.ext
 * e.g. "compressed.tracemonkey-pldi-09.pdf" → "compress....-pldi-09.pdf"
 */
export const truncateFileName = (name: string): string => {
  if (name.length <= 20) return name;
  const lastDot = name.lastIndexOf('.');
  if (lastDot === -1) {
    return `${name.slice(0, 8)}....${name.slice(-8)}`;
  }
  const base = name.slice(0, lastDot);
  const ext = name.slice(lastDot); // includes leading dot, e.g. ".pdf"
  return `${name.slice(0, 8)}....${base.slice(-8)}${ext}`;
};

/** Extracts the file name from a URL and applies truncation. */
export const getFileDisplayName = (url?: string | null): string => {
  if (!url) return '';
  const name = url.split('/').pop() ?? url;
  return truncateFileName(name);
};

/**
 * Truncates a file name that exceeds 15 characters.
 * Format: first6chars...last6charsOfBaseName.ext
 */
export const truncateModalFileName = (name: string): string => {
  if (name.length <= 15) return name;
  const lastDot = name.lastIndexOf('.');
  if (lastDot === -1) {
    return `${name.slice(0, 6)}...${name.slice(-6)}`;
  }
  const base = name.slice(0, lastDot);
  const ext = name.slice(lastDot);
  return `${name.slice(0, 6)}...${base.slice(-6)}${ext}`;
};

/**
 * Truncates a filename for the upload progress display.
 * If the name exceeds 50 characters, returns first 22 chars + '...' + last 22 chars.
 */
export const truncateProgressFileName = (name: string): string => {
  if (name.length <= 50) return name;
  return `${name.slice(0, 22)}...${name.slice(-22)}`;
};

/**
 * This method is used to fetch messages based on filter
 * @param {messageType} - 'All' | 'Read' | 'UnRead'
 * @param {technicianId} - number
 * @param {pageNumber} - number
 * @param {pageSize} - number
 * @returns {Promise<MessageResponse[]>}
 */
export const getMessages = async ({
  messageType,
  technicianId,
  pageNumber,
  pageSize,
  repairId,
}: GetMessagesRequest): Promise<GetMessagesResponse | undefined> => {
  try {
    const params: Record<string, string | boolean | number> = {
      pageNumber,
      pageSize,
    };

    if (technicianId) {
      params.technicianId = technicianId;
    }

    if (repairId) {
      params.repairId = repairId;
    }

    if (messageType === 'Read') {
      params.isRead = true;
    } else if (messageType === 'UnRead') {
      params.isRead = false;
    }
    // 'All' → isRead is not added to params

    const {data} = await apiGet<any>({
      endpoint: `${endPoints.repairDetails.getMessages}`,
      params,
    });
    const response = data as any;
    if (response?.success) {
      const items: MessageResponse[] = response?.data ?? [];
      const totalItems: number =
        response?.pagination?.totalItems ??
        response?.data?.pagination?.totalItems ??
        items.length;
      return {items, totalItems};
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to get the message details by id
 * @param {messageId} - number
 * @returns {MessageResponse[] | undefined}
 */
export const getMessageDetailsById = async (
  messageId: number,
): Promise<MessageResponse[] | undefined> => {
  try {
    const {data} = await apiGet<MessageResponse[]>({
      endpoint: `${endPoints.repairDetails.getMessageById(messageId)}`,
    });
    const response: ApiResponse<MessageResponse[]> = data;
    if (response?.success && response?.data) {
      return response?.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to update a message's read status
 * @param {messageID} - number
 * @param {isRead} - boolean
 * @param {modifiedUserID} - number
 * @param {notificationCount} - number
 * @returns {Promise<string | undefined>}
 */
export const updateMessage = async ({
  messageID,
  isRead,
  modifiedUserID,
  notificationCount,
}: UpdateMessageRequest): Promise<string | undefined> => {
  try {
    const payload = {messageID, isRead, modifiedUserID, notificationCount};
    const {data} = await apiPatch<string>({
      endpoint: `${endPoints.repairDetails.updateMessageById}`,
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response?.success && response?.data) {
      return response?.message;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to truncate the text if it exceeds max length
 * @param {text} - string
 * @param {maxLen} - number
 * @returns {string}
 */
export const truncate = (
  text: string | null | undefined,
  maxLen: number,
): string => {
  if (!text) {
    return '-';
  }
  return text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;
};

// Below is for Add your own request
let _rowCounter = 0;

/**
 * Generates a stable, unique string ID for a request row.
 */
export const createRowId = (): string => `row_${++_rowCounter}_${Date.now()}`;

/**
 * Creates a new empty RequestRow with a unique ID.
 */
export const createEmptyRow = (): RequestRow => ({
  id: createRowId(),
  comment: '',
  quantity: '',
});

/**
 * This method is used to add a new attachment document to a repair / work order
 * @param {AddNewAttachmentRequest}
 * @returns {Promise<string | undefined>}
 */
export const addNewAttachment = async ({
  id: repairId,
  category,
  keyword,
  description,
  fileName,
  fileSize,
  isDocument,
  createdUserID,
  createdTechnicianID,
  fileContent,
}: AddNewAttachmentRequest): Promise<ApiActionResponse | undefined> => {
  try {
    const payload = {
      id: repairId,
      category,
      keyword,
      description,
      fileName,
      fileSize,
      isDocument,
      createdUserID,
      createdTechnicianID,
      fileContent: fileContent ?? '',
    };
    const {data} = await apiPost<string>({
      endpoint: `${endPoints.repairDetails.addNewAssetAttachment}`,
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to create a new attachment for an asset.
 * Uses the asset-specific endpoint: POST /api/assets/{assetId}/attachments
 * Payload shape: { fileBase64, fileName, attachCategoryId, keywords, description, userId }
 * Returns the API response message on success (mirrors the addNewNote pattern).
 * @param {assetId} - string | number
 * @param {attachment} - AddNewAttachmentRequest
 * @returns {Promise<string | undefined>}
 */
export const addAssetAttachment = async (
  assetId: string | number,
  {
    fileName,
    keyword,
    description,
    createdUserID,
    createdTechnicianID,
    fileContent,
    category,
  }: AddNewAttachmentRequest,
): Promise<{isSuccess: boolean; message: string} | undefined> => {
  try {
    const payload = {
      fileBase64: fileContent ?? '',
      fileName,
      attachCategoryId: Number(category),
      keywords: keyword,
      description,
      userId: createdUserID,
      technicianId: createdTechnicianID,
    };
    const {data} = await apiPost<string>({
      endpoint: endPoints.repairDetails.addNewAssetAttachment(assetId),
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to update an existing attachment for an asset.
 * Uses the asset-specific endpoint: PUT /api/assets/{assetId}/attachments/{attachmentId}
 * Payload shape: { fileBase64, fileName, attachCategoryId, keywords, description, userId }
 * Returns the API response message on success (mirrors the addAssetAttachment pattern).
 * @param {assetId} - string | number
 * @param {attachmentId} - string | number
 * @param {attachment} - AddNewAttachmentRequest
 * @returns {Promise<string | undefined>}
 */
export const updateAssetAttachment = async (
  assetId: string | number,
  attachmentId: string | number,
  {
    fileName,
    keyword,
    description,
    createdUserID,
    createdTechnicianID,
    fileContent,
    category,
  }: AddNewAttachmentRequest,
): Promise<{isSuccess: boolean; message: string} | undefined> => {
  try {
    // Only include fileBase64 when the user picked a new file.
    // If no new file is provided (edit mode without re-upload), omit it so
    // the API does not overwrite the existing stored file with an empty value.
    const payload: Record<string, unknown> = {
      fileName,
      attachCategoryId: Number(category),
      keywords: keyword,
      description,
      userId: createdUserID,
      technicianId: createdTechnicianID,
    };
    if (fileContent) {
      payload.fileBase64 = fileContent;
    }
    const {data} = await apiPut<string>({
      endpoint: endPoints.repairDetails.updateAssetAttachment(assetId, attachmentId),
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to create a new attachment for an workOrderId.
 * Uses the asset-specific endpoint: POST /api/workorders/{workOrderId}/attachments
 * Payload shape: { fileBase64, fileName, attachCategoryId, keywords, description, userId }
 * Returns the API response message on success (mirrors the addNewNote pattern).
 * @param {assetId} - string | number
 * @param {attachment} - AddNewAttachmentRequest
 * @returns {Promise<string | undefined>}
 */
export const addWorkOrderAttachment = async (
  id: string | number,
  {
    fileName,
    keyword,
    description,
    createdUserID,
    createdTechnicianID,
    fileContent,
    category,
  }: AddNewAttachmentRequest,
): Promise<{isSuccess: boolean; message: string} | undefined> => {
  try {
    const payload = {
      fileBase64: fileContent ?? '',
      fileName,
      attachCategoryId: Number(category),
      keywords: keyword,
      description,
      userId: createdUserID,
      technicianId: createdTechnicianID,
    };
    const {data} = await apiPost<string>({
      endpoint: endPoints.repairDetails.addNewWorkOrderAttachment(id),
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to update an existing attachment for an workOrder.
 * Uses the asset-specific endpoint: PUT /api/workOrders/{workOrderId}/attachments/{attachmentId}
 * Payload shape: { fileBase64, fileName, attachCategoryId, keywords, description, userId }
 * Returns the API response message on success (mirrors the addAssetAttachment pattern).
 * @param {assetId} - string | number
 * @param {attachmentId} - string | number
 * @param {attachment} - AddNewAttachmentRequest
 * @returns {Promise<string | undefined>}
 */
export const updateWorkOrderAttachment = async (
  assetId: string | number,
  attachmentId: string | number,
  {
    fileName,
    keyword,
    description,
    createdUserID,
    createdTechnicianID,
    fileContent,
    category,
  }: AddNewAttachmentRequest,
): Promise<{isSuccess: boolean; message: string} | undefined> => {
  try {
    // Only include fileBase64 when the user picked a new file.
    // If no new file is provided (edit mode without re-upload), omit it so
    // the API does not overwrite the existing stored file with an empty value.
    const payload: Record<string, unknown> = {
      fileName,
      attachCategoryId: Number(category),
      keywords: keyword,
      description,
      userId: createdUserID,
      technicianId: createdTechnicianID,
    };
    if (fileContent) {
      payload.fileBase64 = fileContent;
    }
    const {data} = await apiPut<string>({
      endpoint: endPoints.repairDetails.updateWorkOrderAttachment(assetId, attachmentId),
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to delete an asset attachment.
 * Uses the asset-specific endpoint: DELETE /api/assets/{assetId}/attachments/{attachmentId}
 * @param {assetId} - string | number
 * @param {attachmentId} - string | number
 * @returns {Promise<string | undefined>}
 */
export const deleteAssetAttachment = async (
  assetId: string | number,
  attachmentId: string | number,
  technicianId?: string | number,
): Promise<{isSuccess: boolean; message: string} | undefined> => {
  try {
    const {data} = await apiDelete<string>({
      endpoint: endPoints.repairDetails.deleteAssetAttachment(assetId, attachmentId, Number(technicianId)),
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
    // 204 No Content — empty body means the delete succeeded
    return {isSuccess: true, message: ''};
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to delete a work order attachment.
 * Uses the work order-specific endpoint: DELETE /api/workorders/{workOrderId}/attachments/{attachmentId}
 * @param {workOrderId} - string | number
 * @param {attachmentId} - string | number
 * @returns {Promise<string | undefined>}
 */
export const deleteWorkOrderAttachment = async (
  workOrderId: string | number,
  attachmentId: string | number,
  technicianId?: string | number,
): Promise<{isSuccess: boolean; message: string} | undefined> => {
  try {
    const {data} = await apiDelete<string>({
      endpoint: endPoints.repairDetails.deleteWorkOrderAttachment(workOrderId, attachmentId, Number(technicianId)),
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
    // 204 No Content — empty body means the delete succeeded
    return {isSuccess: true, message: ''};
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to update the selected note
 * @param {repairId} - number
 * @returns {Promise<TasksList[]>}
 */
export const updateNote = async ({
  noteId,
  documentId,
  subject,
  note,
  isDocument,
  modifiedUserID,
  modifiedTechnicianID,
  isModifiedByTechnician,
}: EditNoteRequest): Promise<ApiActionResponse | undefined> => {
  try {
    const payload = {
      noteId,
      documentId,
      subject,
      note,
      isDocument,
      modifiedUserID,
      modifiedTechnicianID,
      isModifiedByTechnician,
    };
    const {data} = await apiPut<string>({
      endpoint: `${endPoints.repairDetails.editNote}`,
      payload,
    });
    const response: ApiResponse<string> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Deletes a work-order note.
 * Endpoint: DELETE /api/workordernotes/{noteId}/{isRepairNote}/{technicianId}
 * @param {noteId}       - ID of the note to delete
 * @param {isRepairNote} - true for work order notes, false for repair notes
 * @param {technicianId} - ID of the technician performing the delete
 * @returns {Promise<ApiActionResponse | undefined>}
 */
export const deleteNote = async (
  noteId: number,
  isRepairNote: boolean,
  technicianId: number,
): Promise<ApiActionResponse | undefined> => {
  try {
    const {data} = await apiDelete<string>({
      endpoint: endPoints.repairDetails.deleteNote(noteId, isRepairNote, technicianId),
    });
    const response: ApiResponse<string> = data;
    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
      };
    }
    // 204 No Content — empty body means the delete succeeded
    return {isSuccess: true, message: ''};
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Downloads the raw file for an asset attachment.
 * Uses the authenticated apiGet so the Authorization header is included
 * automatically via the axios request interceptor.
 * Endpoint: GET /api/assets/{assetId}/attachments/{attachmentId}/file
 * @param {assetId} - string | number
 * @param {attachmentId} - string | number
 * @returns {Promise<any>} - the API response data (blob/buffer/base64 depending on server)
 */
export const downloadAssetAttachmentFile = async (
  assetId: string | number,
  attachmentId: string | number,
): Promise<any> => {
  try {
    const {data} = await apiGet<any>({
      endpoint: endPoints.repairDetails.getAssetAttachmentFile(assetId, attachmentId),
    });
    return data;
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Downloads the raw file for an asset attachment.
 * Uses the authenticated apiGet so the Authorization header is included
 * automatically via the axios request interceptor.
 * Endpoint: GET /api/assets/{assetId}/attachments/{attachmentId}/file
 * @param {assetId} - string | number
 * @param {attachmentId} - string | number
 * @returns {Promise<any>} - the API response data (blob/buffer/base64 depending on server)
 */
export const downloadWorkOrderAttachmentFile = async (
  workOrderId: string | number,
  attachmentId: string | number,
): Promise<any> => {
  try {
    const {data} = await apiGet<any>({
      endpoint: endPoints.repairDetails.getWorkOrderAttachmentFile(workOrderId, attachmentId),
    });
    return data;
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch the list of meters for an asset.
 * When isMock is false, returns mock data from repairsDetails.json.
 * @param {woId} - number
 * @returns {Promise<MeterItem[] | undefined>}
 */
export const getMeters = async (
  woId: number,
):Promise<MeterItem[] | undefined> => {
  try {
    // if (!appConstants.isMock) {
    //   return repairDetails?.data?.repairDetails?.meters as MeterItem[];
    // }
    const {data} = await apiGet<MeterItem[]>({
      endpoint: `${endPoints.repairDetails.getMeters(woId)}`,
    });
    const response: ApiResponse<MeterItem[]> = data;

    if (response?.success && response?.data) {
      return response?.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to update a meter reading.
 * @param {meterTransactionID} - number
 * @param {reading} - number
 * @param {technicianID} - number
 * @param {modifiedUserID} - number
 * @param {acceptAsInvalid} - boolean
 * @param {override} - boolean
 * @returns {Promise<UpdateMeterResponse | undefined>}
 */
export const updateMeterReading = async ({
  meterTransactionID,
  reading,
  technicianID,
  modifiedUserID,
  acceptAsInvalid,
  override,
}: UpdateMeterRequest): Promise<UpdateMeterResponse | undefined> => {
  try {
    const payload = {
      meterTransactionID,
      reading,
      technicianID,
      modifiedUserID,
      acceptAsInvalid,
      override,
    };
    const {data} = await apiPut<UpdateMeterResponse>({
      endpoint: `${endPoints.repairDetails.updateMeter}`,
      payload,
    });
    const response: ApiResponse<UpdateMeterResponse> = data;

    if (response) {
      return {
        isSuccess: response?.success ?? false,
        message: response?.message ?? '',
        data: (response as any)?.data ?? null,
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch the detail of a meter transaction.
 * @param {meterTransactionId} - number
 * @param {technicianId} - number
 * @returns {Promise<MeterDetail | undefined>}
 */
export const getMeterDetail = async ({
  meterTransactionId,
  technicianId,
}: GetMeterDetailRequest): Promise<MeterDetail | undefined> => {
  try {
    const {data} = await apiGet<MeterDetail>({
      endpoint: `${endPoints.repairDetails.getMeterDetail(meterTransactionId)}`,
      params: {technicianId},
    });
    const response: ApiResponse<MeterDetail> = data;

    if (response?.success && response?.data) {
        return response?.data;
      }
    } catch (error: any) {
      globalErrorHandler(error);
      throw error;
    }
  };
  
  // ---------------------------------------------------------------------------
  // Alert APIs
  // ---------------------------------------------------------------------------
  
  /**
   * Fetches the visibility/enabled state for the Alerts button.
   * GET /api/alerts/asset/{assetId}/button-state
   */
  export const getAlertButtonState = async (
    assetId: number,
  ): Promise<AlertButtonState | undefined> => {
    try {
      const {data} = await apiGet<AlertButtonState>({
        endpoint: endPoints.alerts.getAlertButtonState(assetId),
      });
      const response: ApiResponse<AlertButtonState> = data;
      if (response?.success && response?.data) {
        return response.data;
      }
    } catch (error: any) {
      globalErrorHandler(error);
      throw error;
    }
  };
  
  /**
   * Fetches all alerts for the given asset.
   * GET /api/alerts/asset/{assetId}
   */
  export const getAssetAlerts = async (
    assetId: number,
    pageNumber: number = 1,
    pageSize: number = 9,
    sortBy?: string,
    isAscending?: boolean,
  ): Promise<AssetAlertsApiResponse | undefined> => {
    try {
      const params: Record<string, string | number | boolean> = {pageNumber, pageSize};
      if (sortBy) {
        params.sortBy = sortBy;
        params.isAscending = isAscending ?? true;
      }
      const {data} = await apiGet<any>({
        endpoint: endPoints.alerts.getAssetAlerts(assetId),
        params,
      });
      const response = data as any;
      if (response?.success && response?.data && Array.isArray(response.data)) {
        return {
          data: response.data as AssetAlert[],
          pagination: response.pagination ?? {
            currentPage: pageNumber,
            pageSize,
            totalItems: (response.data as AssetAlert[]).length,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        };
      }
      return undefined;
    } catch (error: any) {
      globalErrorHandler(error);
      throw error;
    }
  };
  
  /**
   * Fetches the latest repair alert for the given repair.
   * GET /api/alerts/repair/{repairId}/alert
   */
  export const getLatestRepairAlert = async (
    repairId: number,
  ): Promise<LatestRepairAlert | undefined> => {
    try {
      const {data} = await apiGet<LatestRepairAlert>({
        endpoint: endPoints.alerts.getLatestRepairAlert(repairId),
      });
      const response: ApiResponse<LatestRepairAlert> = data;
      if (response?.success && response?.data) {
        return response.data;
      }
    } catch (error: any) {
      globalErrorHandler(error);
      throw error;
    }
  };

// ---------------------------------------------------------------------------
// Alerts table sort helpers (reusable)
// ---------------------------------------------------------------------------

export type AlertSortDir = 'asc' | 'desc';

/** Date column keys that require date-based comparison when sorting. */
const ALERT_DATE_KEYS = new Set(['firstOccurrenceDate', 'lastOccurrenceDate']);

/** Numeric column keys — compared as numbers rather than strings. */
const ALERT_NUMERIC_KEYS = new Set(['occurrenceCount', 'alertId']);

/**
 * Compares two AssetAlert rows for the given column key and sort direction.
 * Returns a negative / zero / positive number (Array.sort compareFn contract).
 * @param a   - first alert
 * @param b   - second alert
 * @param key - column key to sort by
 * @param dir - 'asc' | 'desc'
 */
export const compareAlertValues = (
  a: AssetAlert,
  b: AssetAlert,
  key: keyof AssetAlert,
  dir: AlertSortDir,
): number => {
  const rawA = a[key];
  const rawB = b[key];
  let result = 0;
  if (ALERT_DATE_KEYS.has(key)) {
    const da = rawA ? new Date(rawA as string).getTime() : 0;
    const db = rawB ? new Date(rawB as string).getTime() : 0;
    result = da - db;
  } else if (ALERT_NUMERIC_KEYS.has(key)) {
    result = Number(rawA ?? 0) - Number(rawB ?? 0);
  } else {
    const sa = String(rawA ?? '').toLowerCase();
    const sb = String(rawB ?? '').toLowerCase();
    result = sa < sb ? -1 : sa > sb ? 1 : 0;
  }
  return dir === 'asc' ? result : -result;
};

/**
 * Returns a sorted copy of the alerts array by the given column and direction.
 * When key is null the original array is returned unchanged (no allocation).
 * @param alerts - source alert list
 * @param key    - column key to sort by (null = unsorted)
 * @param dir    - 'asc' | 'desc'
 */
export const sortAlerts = (
  alerts: AssetAlert[],
  key: keyof AssetAlert | null,
  dir: AlertSortDir,
): AssetAlert[] => {
  if (!key) return alerts;
  return [...alerts].sort((a, b) => compareAlertValues(a, b, key, dir));
};

/**
 * Sends an authorization-request email to the approver when a WO spending
 * limit is exceeded.
 * Endpoint: POST /api/workorders/request-authorization
 * Returns { success, message }:
 *   success=true  → email sent successfully
 *   success=false → email delivery failed; message contains recipient details
 */
export const requestAuthenticate = async ({
  documentId,
  technicianId,
  userId,
  navigateToHome = false,
  applicationBaseUrl = '',
  applicationVirtualPath = '',
}: RequestAuthenticateParams): Promise<RequestAuthenticateResponse> => {
  try {
    const payload = {
      documentId,
      technicianId,
      navigateToHome,
      userId,
      applicationBaseUrl,
      applicationVirtualPath,
    };
    const {data} = await apiPost<RequestAuthenticateResponse>({
      endpoint: endPoints.repairDetails.requestAuthorization,
      payload,
    });
    const response: ApiResponse<RequestAuthenticateResponse> = data;
    if (response) {
      return {
        success: response?.success ?? false,
        message: response?.message ?? '',
        ...(response?.data ?? {}),
      };
    }
    return {success: false, message: ''};
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};
